/**
 * Ninja Star fly-out — hover/click corridor for ⚡ quick-action menus.
 * Used on task cards (leftward flyout) and chat header (see ninja-star-container--chat).
 */
import { useCallback, useState, type FocusEvent, type ReactNode } from "react";
import { Zap } from "lucide-react";
import { cn } from '@flowstate/utils';
import "@/styles/components/ninja-star.css";

export type NinjaStarVariant = "task" | "chat" | "composer";

export interface NinjaStarFlyoutProps {
  variant?: NinjaStarVariant;
  /** Lightning trigger; defaults to circular ⚡ button */
  trigger?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Flyout slot count — sets leftward hover corridor on task variant */
  itemCount?: number;
  onTriggerClick?: () => void;
  /** Close flyout after a button click inside ninja-star-items (panel toggles) */
  closeOnItemClick?: boolean;
  onOpenChange?: (open: boolean) => void;
  "aria-label"?: string;
  "data-testid"?: string;
}

export function NinjaStarFlyout({
  variant = "task",
  trigger,
  children,
  className,
  itemCount = 8,
  onTriggerClick,
  closeOnItemClick = false,
  onOpenChange,
  "aria-label": ariaLabel = "Quick actions",
  "data-testid": dataTestId,
}: NinjaStarFlyoutProps) {
  const [open, setOpen] = useState(false);
  const corridorPx = itemCount * 44 + 32;

  const setOpenState = useCallback(
    (next: boolean) => {
      setOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  const handleOpen = useCallback(() => setOpenState(true), [setOpenState]);
  const handleClose = useCallback(() => setOpenState(false), [setOpenState]);

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLDivElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        setOpenState(false);
      }
    },
    [setOpenState],
  );

  const handleTriggerClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setOpenState(!open);
      onTriggerClick?.();
    },
    [onTriggerClick, open, setOpenState],
  );

  const handleItemsClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!closeOnItemClick) return;
      const target = e.target as HTMLElement;
      if (target.closest("button, [role='menuitem'], a")) {
        setOpenState(false);
      }
    },
    [closeOnItemClick, setOpenState],
  );

  return (
    <div
      className={cn(
        "ninja-star-container",
        variant === "chat" && "ninja-star-container--chat relative",
        variant === "composer" && "ninja-star-container--chat ninja-star-container--composer relative",
        variant === "task" && "ninja-star-container--task",
        className,
      )}
      style={
        variant === "task"
          ? ({ ["--ninja-corridor" as string]: `${corridorPx}px` } as React.CSSProperties)
          : undefined
      }
      data-open={open ? "true" : "false"}
      data-testid={dataTestId}
      onMouseEnter={handleOpen}
      onMouseLeave={handleClose}
      onFocus={handleOpen}
      onBlur={handleBlur}
    >
      {trigger ? (
        <div className="ninja-star-trigger-slot" onClick={handleTriggerClick}>
          {trigger}
        </div>
      ) : (
        <button
          type="button"
          className="lightning-trigger"
          aria-label={ariaLabel}
          aria-expanded={open}
          onClick={handleTriggerClick}
        >
          <Zap className="h-4 w-4" aria-hidden />
        </button>
      )}
      <div className="ninja-star-items" onClick={handleItemsClick}>
        {children}
      </div>
    </div>
  );
}
